import axios, {AxiosInstance} from 'axios'
import axiosRetry, {exponentialDelay, isNetworkOrIdempotentRequestError} from 'axios-retry'
import jwtDecode from 'jwt-decode'

type Token = {
    exp: number
}

const SCOPE = 'openid'
const RESPONSE_TYPE = 'id_token'
const TIMEOUT_BUFFER_MILLIS = 60 * 1000
const MIDWAY_URL = 'https://midway-auth.amazon.com'
const MIDWAY_PARAMS = ['id_token']

const removeSearchParams = (queryString: string, paramsToRemove: string[]) => {
    const searchParams = new URLSearchParams(queryString)
    for (const param of paramsToRemove) {
        searchParams.delete(param)
    }
    return searchParams.toString()
}

const decodeJwt = (token: string) => jwtDecode<Token>(token)

const generateRandomString = () => Math.random().toString(36).substring(2, 15)

const getMidwayQueryParams = (): Record<string, string> => ({
    scope: SCOPE,
    response_type: RESPONSE_TYPE,
    client_id: window.location.origin,
    redirect_uri: window.location.href,
    nonce: `${generateRandomString()}${generateRandomString()}`,
})

const createDefaultAxiosClient = () => {
    const apiClient = axios.create({baseURL: MIDWAY_URL})
    axiosRetry(apiClient, {
        retries: 5,
        retryDelay: (retryCount) => exponentialDelay(retryCount),
        // Due to security risk, the browser hides the real response when error is due to CORS. Hence, when the response
        // is undefined, the error can be due to the network or CORS. It isn't possible to distinguish between the two,
        // so do not retry when the response is undefined. CORS errors are usually observed on mobile devices.
        retryCondition: (error) => error.response !== undefined && isNetworkOrIdempotentRequestError(error),
    })
    apiClient.interceptors.response.use((_) => _, (error) => {
        window.location.replace(getMidwayRedirectUrl())
        throw error
    })

    return apiClient
}

const getTokenExpiryInMillis = async (tokenPromise: Promise<string>): Promise<number> => {
    const token = await tokenPromise
    const decodedToken = decodeJwt(token)
    return decodedToken.exp * 1000
}

const makeTokenRequest = async (apiClient: AxiosInstance) => {
    const {data} = await apiClient.get<string>('/SSO', {
        params: getMidwayQueryParams(),
        withCredentials: true,
        headers: {accept: '*/*'},
    })
    return data
}

export const getMidwayRedirectUrl = () =>
    `${MIDWAY_URL}/SSO/redirect?${new URLSearchParams(getMidwayQueryParams()).toString()}`

export const getMidwayHelper = (apiClient: AxiosInstance = createDefaultAxiosClient()) => {
    let cachedTokenPromise: Promise<string> | null = null
    let refreshTokenTimeoutId = 0

    const queryStringWithoutMidwayParams = removeSearchParams(window.location.search, MIDWAY_PARAMS)
    const urlWithoutMidwayParams = queryStringWithoutMidwayParams === '' ?
        window.location.pathname :
        `${window.location.pathname}?${queryStringWithoutMidwayParams}`
    window.history.replaceState(null, '', urlWithoutMidwayParams)

    const startRefreshInBackground = async () => {
        if (!cachedTokenPromise) {
            return
        }

        const tokenPromise = cachedTokenPromise
        const expiryInMillis = await getTokenExpiryInMillis(tokenPromise)

        // Condition exists to prevent a race condition. The token promise might have been updated by other
        // incoming requests while waiting for the token promise to resolve.
        if (cachedTokenPromise === tokenPromise) {
            const timeoutInMillis = expiryInMillis - Date.now() - TIMEOUT_BUFFER_MILLIS

            clearTimeout(refreshTokenTimeoutId)
            refreshTokenTimeoutId = window.setTimeout(() => {
                cachedTokenPromise = makeTokenRequest(apiClient)
                void startRefreshInBackground()
            }, timeoutInMillis)
        }
    }

    const getToken = async (): Promise<string> => {
        const tokenPromise = cachedTokenPromise
        let shouldUpdateToken = true

        if (tokenPromise) {
            const expiryInMillis = await getTokenExpiryInMillis(tokenPromise)
            if (Date.now() < expiryInMillis) {
                shouldUpdateToken = false
            }
        }

        // Last condition exists to prevent a race condition. The token promise might have been updated by other
        // incoming requests while waiting for the token promise to resolve.
        if (shouldUpdateToken && cachedTokenPromise === tokenPromise) {
            cachedTokenPromise = makeTokenRequest(apiClient)
            void startRefreshInBackground()
        }

        return cachedTokenPromise!
    }

    return {getToken}
}
